// src/components/backoffice/calculation-parameters/CalculationParametersPage.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { 
  getCalculationParameters, 
  getCalculationParameterDetails, 
  saveCalculationParameter,
  CalculationParameter, 
  SaveParameterData 
} from '../../../services/backoffice/calculationParameterService';

const CalculationParametersPage: React.FC = () => {
  // State for parameters list
  const [parameters, setParameters] = useState<CalculationParameter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // State for modal
  const [showModal, setShowModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // State for form
  const [formData, setFormData] = useState<SaveParameterData>({
    parameter_name: '',
    parameter_key: '',
    value: 0,
    value_type: 'percentage',
    description: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Load parameters from server
  const loadParameters = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔧 [DEBUG] Loading calculation parameters...');
      const response = await getCalculationParameters();
      console.log('🔧 [DEBUG] Parameters loaded successfully:', response);
      
      setParameters(response.parameters);
      
    } catch (error: any) {
      console.error('🔧 [DEBUG] Error loading parameters:', error);
      
      // טיפול משופר בשגיאות (כולל session expiry)
      if (error.message === 'Session expired - redirecting to login') {
        // handleSessionError כבר טיפל בהפניה, אין צורך לעשות כלום
        return;
      }
      
      setError(`שגיאה בטעינת הפרמטרים: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load parameters on component mount
  useEffect(() => {
    loadParameters();
  }, [loadParameters]);

  // Reset form
  const resetForm = () => {
    setFormData({
      parameter_name: '',
      parameter_key: '',
      value: 0,
      value_type: 'percentage',
      description: ''
    });
    setFormErrors({});
  };

  // Handle create new parameter
  const handleCreateNew = () => {
    setIsCreating(true);
    resetForm();
    setShowModal(true);
  };

  // Handle edit parameter
  const handleEdit = async (parameterId: number) => {
    try {
      console.log('🔧 [DEBUG] Loading parameter details for ID:', parameterId);
      const parameterDetails = await getCalculationParameterDetails(parameterId);
      console.log('🔧 [DEBUG] Parameter details loaded:', parameterDetails);
      
      setIsCreating(false);
      setFormData({
        id: parameterDetails.id,
        parameter_name: parameterDetails.parameter_name,
        parameter_key: parameterDetails.parameter_key,
        value: parameterDetails.value,
        value_type: parameterDetails.value_type,
        description: parameterDetails.description || ''
      });
      setFormErrors({});
      setShowModal(true);
    } catch (error: any) {
      console.error('🔧 [DEBUG] Error loading parameter details:', error);
      
      // טיפול בשגיאות כולל session expiry
      if (error.message === 'Session expired - redirecting to login') {
        return;
      }
      
      setError(`שגיאה בטעינת פרטי הפרמטר: ${error.message}`);
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Parameter name validation
    if (!formData.parameter_name?.trim()) {
      errors.parameter_name = 'שם הפרמטר נדרש';
    } else if (formData.parameter_name.trim().length < 2) {
      errors.parameter_name = 'שם הפרמטר חייב להכיל לפחות 2 תווים';
    }

    // Parameter key validation
    if (!formData.parameter_key?.trim()) {
      errors.parameter_key = 'מפתח הפרמטר נדרש';
    } else if (!/^[a-z_]+$/.test(formData.parameter_key)) {
      errors.parameter_key = 'מפתח הפרמטר יכול להכיל רק אותיות אנגליות קטנות וקו תחתון';
    }

    // Value validation
    if (isNaN(formData.value)) {
      errors.value = 'ערך לא תקין';
    }

    // Value type validation
    if (!['percentage', 'fixed', 'formula'].includes(formData.value_type)) {
      errors.value_type = 'סוג ערך לא תקין';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      console.log('🔧 [DEBUG] Submitting parameter data:', formData);
      await saveCalculationParameter(formData);
      console.log('🔧 [DEBUG] Parameter saved successfully');
      
      setShowModal(false);
      await loadParameters();
      resetForm();
      
      if (isCreating) {
        alert('הפרמטר נוצר בהצלחה');
      } else {
        alert('הפרמטר עודכן בהצלחה');
      }
    } catch (error: any) {
      console.error('🔧 [DEBUG] Error saving parameter:', error);
      
      // טיפול בשגיאות כולל session expiry
      if (error.message === 'Session expired - redirecting to login') {
        return;
      }
      
      setError(`שגיאה בשמירת הפרמטר: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle close modal
  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get value type display text
  const getValueTypeText = (valueType: string): string => {
    switch (valueType) {
      case 'percentage': return 'אחוז';
      case 'fixed': return 'ערך קבוע';
      case 'formula': return 'נוסחה';
      default: return valueType;
    }
  };

  // Format value display
  const formatValue = (value: number, valueType: string): string => {
    if (valueType === 'percentage') {
      return `${value}%`;
    }
    return value.toString();
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">🔧 ניהול פרמטרי חישוב</h1>
        <button
          onClick={handleCreateNew}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          + הוספת פרמטר חדש
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Parameters Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="text-gray-500">טוען פרמטרים...</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    מזהה
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    שם הפרמטר
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    מפתח
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ערך
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    סוג ערך
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    תיאור
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    עודכן
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    פעולות
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {parameters.map((parameter) => (
                  <tr key={parameter.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {parameter.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {parameter.parameter_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {parameter.parameter_key}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                      {formatValue(parameter.value, parameter.value_type)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getValueTypeText(parameter.value_type)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {parameter.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(parameter.ts)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEdit(parameter.id)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        ערוך
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {isCreating ? 'הוספת פרמטר חדש' : 'עריכת פרמטר'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Parameter Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    שם הפרמטר *
                  </label>
                  <input
                    type="text"
                    value={formData.parameter_name}
                    onChange={(e) => setFormData({ ...formData, parameter_name: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.parameter_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="לדוגמה: מע״מ רגיל"
                  />
                  {formErrors.parameter_name && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.parameter_name}</p>
                  )}
                </div>

                {/* Parameter Key */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    מפתח הפרמטר *
                  </label>
                  <input
                    type="text"
                    value={formData.parameter_key}
                    onChange={(e) => setFormData({ ...formData, parameter_key: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono ${
                      formErrors.parameter_key ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="לדוגמה: vat_regular"
                  />
                  {formErrors.parameter_key && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.parameter_key}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    רק אותיות אנגליות קטנות וקו תחתון
                  </p>
                </div>

                {/* Value Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    סוג ערך *
                  </label>
                  <select
                    value={formData.value_type}
                    onChange={(e) => setFormData({ ...formData, value_type: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="percentage">אחוז</option>
                    <option value="fixed">ערך קבוע</option>
                    <option value="formula">נוסחה</option>
                  </select>
                </div>

                {/* Value */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ערך *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: parseFloat(e.target.value) || 0 })}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.value ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="17"
                  />
                  {formErrors.value && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.value}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    תיאור
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="תיאור אופציונלי של הפרמטר"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md transition-colors disabled:opacity-50"
                  >
                    {saving ? 'שומר...' : 'שמור'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-md transition-colors"
                  >
                    בטל
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalculationParametersPage;